/**
 * A certificate in the admin's own words.
 *
 * The standard tab only offers a volunteer once the API has approved hours to
 * put on the document (§4.29) — by design: a certificate that named no work
 * would not be honest. This tab is the other door. Sometimes what is being
 * recognised was never an attendance row at all — a donor, a one-off
 * contribution, a volunteer whose work predates this system — so here the
 * admin writes the citation directly, the way a person signs a letter, for
 * any volunteer regardless of status or hours (migration 044).
 *
 * The directory below is deliberately the whole volunteer list, browsable and
 * paged like the approved-hours table and the Volunteers screen itself —
 * not a type-to-search box — because eligibility here is "exists", and a
 * screen that only reveals someone once you have typed their name is a poor
 * way to say that.
 *
 * Same document, same rules as the standard tab: the certificate font is
 * Latin-only and this refuses before anything is written if the text cannot
 * be printed, the row is a snapshot the moment it is issued, and it is
 * emailed the same way if the volunteer has an address on file.
 */

import { useCallback, useState } from 'react';
import { CUSTOM_CERTIFICATE_TEXT_MAX_LENGTH, VOLUNTEER_STATUSES, personName } from '@parinaam-vms/shared';
import type { VolunteerListItem } from '@parinaam-vms/shared';
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  Pager,
  SelectInput,
  StatusBadge,
  TextArea,
  TextInput,
} from '../../components/common';
import type { Column } from '../../components/common';
import { ROWS_PER_PAGE } from '../../constants/pagination';
import { useVolunteers } from '../../hooks/useVolunteers';
import { useDownloadCertificate, useIssueCustomCertificate } from '../../hooks/useCertificates';
import { fetchCustomPreviewPdf } from '../../services/api/certificates';
import { CertificateViewer } from './CertificateViewer';
import { humaniseCode } from '../../utils/format';
import styles from './CertificatesPage.module.css';

type ChosenVolunteer = Pick<VolunteerListItem, 'volunteer_id' | 'email'> & { name: string };

const STATUS_OPTIONS = VOLUNTEER_STATUSES.map((status) => ({
  value: status,
  label: humaniseCode(status),
}));

export function CustomCertificateTab() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [chosen, setChosen] = useState<ChosenVolunteer | null>(null);
  const [text, setText] = useState('');
  /*
   * The wording the preview was asked for, not the wording in the box.
   *
   * Held separately so that typing after opening a preview does not refetch a
   * PDF on every keystroke — the draft on screen is the one that was asked
   * for, and the Preview button is how you ask for a newer one.
   */
  const [previewText, setPreviewText] = useState<string | null>(null);

  const issue = useIssueCustomCertificate();
  const download = useDownloadCertificate();

  const query = useVolunteers({
    search: search.trim() || undefined,
    status: status || undefined,
    page,
    page_size: ROWS_PER_PAGE,
  });
  const rows = query.data?.items ?? [];

  function choose(volunteer: VolunteerListItem) {
    setChosen({
      volunteer_id: volunteer.volunteer_id,
      email: volunteer.email,
      name: personName(volunteer),
    });
    setText('');
    issue.reset();
  }

  function changeVolunteer() {
    setChosen(null);
    setText('');
    setPreviewText(null);
    issue.reset();
  }

  const chosenId = chosen?.volunteer_id;
  const loadPreview = useCallback(() => {
    if (!chosenId || previewText === null) {
      return Promise.reject(new Error('Nothing to preview.'));
    }
    return fetchCustomPreviewPdf(chosenId, previewText);
  }, [chosenId, previewText]);

  const remaining = CUSTOM_CERTIFICATE_TEXT_MAX_LENGTH - text.length;
  const canIssue = Boolean(chosen) && text.trim().length > 0 && remaining >= 0 && !issue.isPending;

  const columns: Column<VolunteerListItem>[] = [
    { key: 'code', header: 'ID', render: (row) => row.volunteer_code },
    {
      key: 'name',
      header: 'Volunteer',
      render: (row) => (
        <div className={styles.nameCell}>
          <span className={styles.name}>{personName(row)}</span>
          {row.organization_name && <span className={styles.subtle}>{row.organization_name}</span>}
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Email',
      render: (row) =>
        row.email ?? (
          <span className={styles.noEmail} title="No email on file — the certificate can only be downloaded">
            none on file
          </span>
        ),
    },
    { key: 'status', header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    {
      key: 'action',
      header: '',
      render: (row) => (
        <Button variant="primary" onClick={() => choose(row)}>
          Write certificate
        </Button>
      ),
    },
  ];

  return (
    <Card>
      <p className={styles.intro}>
        Write a certificate for any volunteer, in your own words — for recognition that was
        never recorded as hours: a one-off contribution, a donation of time this system does
        not track, or a volunteer from before this system existed. It uses the same design as
        a standard certificate and is emailed the same way if the volunteer has an address on
        file.
      </p>

      {chosen && (
        <div className={styles.chosenVolunteer}>
          <span>
            Writing a certificate for <strong>{chosen.name}</strong>
            {!chosen.email && (
              <span className={styles.noEmail}>
                {' '}
                — no email on file, so it will be downloaded and handed over
              </span>
            )}
          </span>
          <Button onClick={changeVolunteer} disabled={issue.isPending}>
            Choose someone else
          </Button>
        </div>
      )}

      {chosen ? (
        <>
          <TextArea
            label="Certificate text"
            placeholder="e.g. In recognition of five years of dedicated service to the Read to Rise programme."
            value={text}
            onChange={(event) => setText(event.target.value)}
            rows={5}
            disabled={issue.isPending}
          />
          <p className={remaining < 0 ? styles.overCap : styles.subtle}>
            {remaining} characters remaining
          </p>

          {issue.isError && (
            <p className={styles.error} role="alert">
              {issue.error.message}
            </p>
          )}

          {/*
            Preview before Issue, in the order the decision is made. Issuing
            this wording cannot be undone and emails somebody, so reading the
            document first is the point of the whole feature.
          */}
          <div className={styles.actions}>
            <Button
              variant="secondary"
              disabled={text.trim().length === 0 || remaining < 0 || issue.isPending}
              onClick={() => setPreviewText(previewText === null ? text.trim() : null)}
            >
              {previewText === null ? 'Preview' : 'Hide preview'}
            </Button>
            <Button
              variant="primary"
              disabled={!canIssue}
              loading={issue.isPending}
              onClick={() =>
                issue.mutate(
                  { volunteer_id: chosen.volunteer_id, custom_text: text.trim() },
                  {
                    onSuccess: () => {
                      setText('');
                      setPreviewText(null);
                    },
                  }
                )
              }
            >
              Issue certificate
            </Button>
          </div>

          {previewText !== null && (
            <CertificateViewer
              title="Draft — not yet issued"
              subtitle={`What ${chosen.name}'s certificate would say with this wording. Nothing has been sent.`}
              source={loadPreview}
              downloadName="Parinaam-Certificate-DRAFT.pdf"
              onClose={() => setPreviewText(null)}
            />
          )}

          {issue.data && (
            <div className={styles.issuedList}>
              <span className={styles.issuedCode}>{issue.data.certificate_code}</span>{' '}
              issued to {chosen.name}.{' '}
              <Button
                onClick={() =>
                  download.mutate({
                    certificate_id: issue.data!.certificate_id,
                    certificate_code: issue.data!.certificate_code,
                  })
                }
                loading={download.isPending}
              >
                Download PDF
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          <div className={styles.filters}>
            <TextInput
              label="Search"
              value={search}
              placeholder="Name, code, email or city"
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
            <SelectInput
              label="Status"
              placeholder="All statuses"
              value={status}
              options={STATUS_OPTIONS}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            />
          </div>

          {query.isPending ? (
            <LoadingState label="Loading volunteers…" />
          ) : query.isError ? (
            <ErrorState error={query.error} onRetry={() => void query.refetch()} />
          ) : rows.length === 0 ? (
            <EmptyState
              title="No volunteers found"
              message={
                search || status
                  ? 'No volunteer matches these filters.'
                  : 'Volunteers appear here once they are registered or added by staff.'
              }
            />
          ) : (
            <>
              <DataTable<VolunteerListItem>
                caption="Volunteers"
                rows={rows}
                columns={columns}
                getRowKey={(row) => row.volunteer_id}
              />
              <Pager pagination={query.data?.pagination} onPageChange={setPage} />
            </>
          )}
        </>
      )}
    </Card>
  );
}
